import { X } from "lucide-react";

import { cn } from "@/lib/utils";

// ===========================================================================
// LOS FILTROS DE CUENTA, EMPRESA Y MONEDA.
//
// Contabilidad los pidió en la portada —«filtrar por grupo de razón social,
// cuentas, moneda»— y en la mesa —«no funciona ningún filtro»—. Van en un solo
// sitio porque son la misma pregunta: sobre qué dinero estoy mirando.
//
// Las opciones se sacan de lo que HAY, no de una lista fija: una lista fija se
// queda vieja en cuanto entra un banco nuevo y entonces el filtro esconde
// movimientos sin decirlo. Y solo salen cuentas y empresas CON movimientos —
// ofrecer las 154 del maestro cuando 6 tienen extracto son 148 filtros que dan
// cero.
//
// Lo que está filtrado se ve SIEMPRE, con su ✕. Una lista recortada sin decir
// por qué es la forma más fácil de que alguien crea que faltan datos.
// ===========================================================================

export type OpcionesDeFiltro = {
  cuentas: Array<{
    id: string;
    banco: string;
    numero: string;
    moneda: "PEN" | "USD";
  }>;
  empresas: Array<{ id: string; razonSocial: string }>;
};

export type FiltroElegido = {
  empresaId?: string;
  cuentaId?: string;
  moneda?: "PEN" | "USD";
};

/** Los últimos cuatro dígitos, que es como se nombra una cuenta al hablar. */
function corta(numero: string): string {
  return numero.length > 4 ? `····${numero.slice(-4)}` : numero;
}

export function Filtros({
  opciones,
  valor,
  alCambiar,
}: {
  opciones: OpcionesDeFiltro;
  valor: FiltroElegido;
  alCambiar: (v: FiltroElegido) => void;
}) {
  const hayAlgo = Boolean(valor.empresaId || valor.cuentaId || valor.moneda);

  // Con una sola empresa el desplegable no aporta: elegir entre una opción no
  // es elegir. Se dibuja cuando hay de dónde escoger.
  const conEmpresas = opciones.empresas.length > 1;

  return (
    <div className="flex flex-wrap items-center gap-2 text-[12px]">
      {conEmpresas ? (
        <Selector
          etiqueta="Empresa"
          valor={valor.empresaId ?? ""}
          alElegir={(v) => alCambiar({ ...valor, empresaId: v || undefined })}
          opciones={opciones.empresas.map((e) => ({
            valor: e.id,
            texto: e.razonSocial,
          }))}
          todos="Todas las empresas"
        />
      ) : null}

      <Selector
        etiqueta="Cuenta"
        valor={valor.cuentaId ?? ""}
        alElegir={(v) => alCambiar({ ...valor, cuentaId: v || undefined })}
        opciones={opciones.cuentas.map((c) => ({
          valor: c.id,
          texto: `${c.banco} ${corta(c.numero)} · ${c.moneda === "USD" ? "US$" : "S/"}`,
        }))}
        todos="Todas las cuentas"
      />

      <Selector
        etiqueta="Moneda"
        valor={valor.moneda ?? ""}
        alElegir={(v) =>
          alCambiar({ ...valor, moneda: (v || undefined) as "PEN" | "USD" })
        }
        opciones={[
          { valor: "PEN", texto: "Soles" },
          { valor: "USD", texto: "Dólares" },
        ]}
        todos="Las dos monedas"
      />

      {hayAlgo ? (
        <button
          type="button"
          onClick={() => alCambiar({})}
          className="flex items-center gap-1 rounded-md border px-2 py-1 text-muted-foreground transition-colors hover:bg-muted"
        >
          <X className="size-3" />
          Quitar los filtros
        </button>
      ) : null}
    </div>
  );
}

function Selector({
  etiqueta,
  valor,
  alElegir,
  opciones,
  todos,
}: {
  etiqueta: string;
  valor: string;
  alElegir: (v: string) => void;
  opciones: Array<{ valor: string; texto: string }>;
  todos: string;
}) {
  return (
    <label className="flex items-center gap-1.5">
      <span className="text-muted-foreground">{etiqueta}</span>
      <select
        value={valor}
        onChange={(e) => alElegir(e.target.value)}
        className={cn(
          "h-[30px] rounded-md border bg-background px-2 text-[12px] outline-none",
          // Lo filtrado se ve: un desplegable con un valor puesto que parece
          // vacío es cómo alguien acaba creyendo que faltan datos.
          valor &&
            "border-cyan-500 font-medium text-cyan-700 dark:text-cyan-400",
        )}
      >
        <option value="">{todos}</option>
        {opciones.map((o) => (
          <option key={o.valor} value={o.valor}>
            {o.texto}
          </option>
        ))}
      </select>
    </label>
  );
}
